import { useAtomValue } from 'jotai';
import { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { twJoin } from 'tailwind-merge';
import { tableColumnsEditorActiveTabAtom } from '@/atoms/table';
import { CheckboxPicker } from '@/components/checkboxPicker/CheckboxPicker';
import { useGroupedOptions } from '@/hooks/useGroupedOptions';
import { BaseOption } from '@/types/generic';
import { ColumnGroup, TableColumnsEditorTabs } from '@/types/table';
import { mapGroupOptions, toCheckboxGroups } from '@/utils/checkboxPicker';

type OnSelectionChange = ComponentProps<typeof CheckboxPicker>['onSelectionChange'];

export const ColumnSelect = ({
  columnGroups,
  visibleColumns,
  setVisibleColumns,
  lockedColumns = [],
}: {
  columnGroups: ColumnGroup[];
  visibleColumns: string[];
  setVisibleColumns: (newVisibleColumns: string[]) => void;
  lockedColumns?: string[];
}) => {
  const { t } = useTranslation(['table', 'filters']);
  const activeTab = useAtomValue(tableColumnsEditorActiveTabAtom);

  const sortedTranslatedColumnGroups = useGroupedOptions(columnGroups);

  // tooltips are resolved centrally in useGroupedOptions; here we only mark locked columns as disabled
  const markLocked = (option: BaseOption<string>) => ({
    ...option,
    optional: { ...option.optional, isDisabled: lockedColumns.includes(option.value) },
  });

  const groups = toCheckboxGroups(
    mapGroupOptions(sortedTranslatedColumnGroups, markLocked),
    t('metricThreeSixtyIndicator', { ns: 'filters' }),
    t('metricPhysicalIndicator', { ns: 'filters' }),
  );

  // modalVisibleColumns doubles as the table column order consumed by the sibling ColumnSort, so we
  // preserve the existing order rather than storing the picker's group-ordered array. Locked columns
  // are always retained.
  const onSelectionChange: OnSelectionChange = (_event, nextValues) => {
    const selectedSet = new Set([...nextValues, ...lockedColumns]);
    const visibleSet = new Set(visibleColumns);
    const columnsToKeep = visibleColumns.filter(value => selectedSet.has(value));
    const newColumns = nextValues.filter(value => !visibleSet.has(value));
    setVisibleColumns([...columnsToKeep, ...newColumns]);
  };

  return (
    <div
      className={twJoin(
        'w-full md:!w-3/5 overflow-y-auto',
        activeTab !== TableColumnsEditorTabs.COLUMN_SELECT && 'hidden md:flex',
      )}
    >
      <CheckboxPicker
        groups={groups}
        title={t('availableColumns')}
        searchPlaceholder={t('searchField')}
        selectedValues={visibleColumns}
        onSelectionChange={onSelectionChange}
        selectAll
      />
    </div>
  );
};
